<template>
    <rkm-form-group
        v-if="range"
        label-font-weight="normal"
        class="mb-0"
    >
        <label v-if="label">
            {{ label }}
        </label>

        <rkm-row>
            <rkm-col>
                <ValidationProvider
                    :mode="validationMode"
                    :rules="startRules"
                    :name="localStartLabel"
                    slim
                    :vid="`${id}-inicio`"
                    v-slot="{ validated, valid, errors }"
                >
                    <rkm-form-group
                        :invalid-feedback="errors[0]"
                        :state="startRules ? (validated ? valid : null) : null"
                        v-bind="rangeOptionalProps"
                    >
                        <label :for="`${id}-inicio`">
                            {{ localStartLabel + (startRules && startRules.required ? ' *' : '') }}
                        </label>

                        <template v-if="tooltipText">
                            <rkm-tooltip
                                :placement="tooltipPosition"
                                :target="uniqueId('FormDatePickerTooltip')"
                            >
                                {{ tooltipText }}
                            </rkm-tooltip>

                            <rkm-icon
                                :id="uniqueId('FormDatePickerTooltip')"
                                class="ml-1"
                                icon="info-circle"
                            />
                        </template>

                        <b-input-group>
                            <b-form-input
                                placeholder="DD/MM/AAAA"
                                :disabled="disabled"
                                :id="`${id}-inicio`"
                                :readonly="readonly"
                                :state="startRules ? (validated ? valid : null) : null"
                                :value="formattedStartValue"
                                @blur="handleStartBlur"
                                ref="DateStartInput"
                                type="text"
                            />

                            <b-input-group-append>
                                <b-form-datepicker
                                    calendar-width="240px"
                                    hide-header
                                    :disabled="disabled"
                                    :max="localStartMaxDate"
                                    :min="localStartMinDate"
                                    :readonly="readonly"
                                    :right="right"
                                    :state="startRules ? (validated ? valid : null) : null"
                                    button-only
                                    class="rkm-date-picker"
                                    locale="pt-BR"
                                    value-as-date
                                    v-bind="labelProperties"
                                    v-model="value.start"
                                >
                                    <template #button-content>
                                        <rkm-icon :icon="['far', 'calendar-alt']" />
                                    </template>
                                </b-form-datepicker>
                            </b-input-group-append>
                        </b-input-group>
                    </rkm-form-group>
                </ValidationProvider>
            </rkm-col>

            <rkm-col>
                <ValidationProvider
                    :mode="validationMode"
                    :rules="endRules"
                    :name="localEndLabel"
                    slim
                    :vid="`${id}-fim`"
                    v-slot="{ validated, valid, errors }"
                >
                    <rkm-form-group
                        :invalid-feedback="errors[0]"
                        :state="endRules ? (validated ? valid : null) : null"
                        v-bind="rangeOptionalProps"
                    >
                        <label :for="`${id}-fim`">
                            {{ localEndLabel + (endRules && endRules.required ? ' *' : '') }}
                        </label>

                        <b-input-group>
                            <b-form-input
                                placeholder="DD/MM/AAAA"
                                :disabled="disabled"
                                :id="`${id}-fim`"
                                :readonly="readonly"
                                :state="endRules ? (validated ? valid : null) : null"
                                :value="formattedEndValue"
                                @blur="handleEndBlur"
                                ref="DateEndInput"
                                type="text"
                            />

                            <b-input-group-append>
                                <b-form-datepicker
                                    calendar-width="240px"
                                    hide-header
                                    :disabled="disabled"
                                    :max="localEndMaxDate"
                                    :min="localEndMinDate"
                                    :readonly="readonly"
                                    :right="right"
                                    :state="endRules ? (validated ? valid : null) : null"
                                    button-only
                                    class="rkm-date-picker"
                                    locale="pt-BR"
                                    value-as-date
                                    v-bind="labelProperties"
                                    v-model="value.end"
                                >
                                    <template #button-content>
                                        <rkm-icon :icon="['far', 'calendar-alt']" />
                                    </template>
                                </b-form-datepicker>
                            </b-input-group-append>
                        </b-input-group>
                    </rkm-form-group>
                </ValidationProvider>
            </rkm-col>
        </rkm-row>
    </rkm-form-group>

    <ValidationProvider
        v-else
        :mode="validationMode"
        :name="label || name || 'unknown'"
        :rules="rules"
        slim
        :vid="id"
        v-slot="{ validated, valid, errors }"
    >
        <rkm-form-group
            :invalid-feedback="errors[0]"
            :state="rules ? (validated ? valid : null) : null"
        >
            <template v-if="label">
                <div class="float-left">
                    <label :for="id">
                        {{ label + (rules && rules.required ? ' *' : '') }}
                    </label>
                </div>

                <slot name="label-right" />
            </template>

            <b-input-group>
                <b-form-input
                    placeholder="DD/MM/AAAA"
                    :disabled="disabled"
                    :id="id"
                    :readonly="readonly"
                    :state="rules ? (validated ? valid : null) : null"
                    :value="formattedValue"
                    @blur="handleBlur"
                    ref="DateInput"
                    type="text"
                />

                <b-input-group-append>
                    <b-form-datepicker
                        calendar-width="240px"
                        hide-header
                        :disabled="disabled"
                        :max="localMaxDate"
                        :min="localMinDate"
                        :readonly="readonly"
                        :right="right"
                        :state="rules ? (validated ? valid : null) : null"
                        button-only
                        class="rkm-date-picker"
                        locale="pt-BR"
                        v-bind="labelProperties"
                        value-as-date
                        :value="value"
                        @input="$emit('input', $event)"
                    >
                        <template #button-content>
                            <rkm-icon :icon="['far', 'calendar-alt']" />
                        </template>
                    </b-form-datepicker>
                </b-input-group-append>
            </b-input-group>
        </rkm-form-group>
    </ValidationProvider>
</template>

<script>
import Cleave from 'cleave.js'
import moment from 'moment'
import { ValidationProvider } from 'vee-validate'
import { BInputGroup, BFormInput, BInputGroupAppend, BFormDatepicker } from 'bootstrap-vue'

export default {
    components: { ValidationProvider, BInputGroup, BFormInput, BInputGroupAppend, BFormDatepicker },
    computed: {
        id() {

            // TODO: Centralizar e a tratar melhor a string, removendo caracteres especiais, acentos, etc.
            const id = (this.label || 'unknown')
                .toLowerCase()
                .replace(/[()\\/\-<>]/gi, '')
                .replace(/[\s]/gi, '-')

            return this.uniqueId(id)
        },
        rules() {
            return this.disabled ? null : this.localValidations
        },
        startRules() {
            return this.disabled ? null : this.localStartValidations
        },
        endRules() {
            return this.disabled ? null : this.localEndValidations
        },
        localMinDate() {

            const date = moment(this.minDate, this.dateFormat)

            return date.isValid() ? date.toDate() : null
        },
        localMaxDate() {

            const date = moment(this.maxDate, this.dateFormat)

            return date.isValid() ? date.toDate() : null
        },
        localStartMinDate() {

            if (this.value.end && !this.noDefaultRangeLimit) {

                const endLessOneYear = moment(this.value.end).subtract(1, 'year').toDate()

                if (!this.localMinDate || this.localMinDate < endLessOneYear) {

                    return endLessOneYear
                }
            }

            return this.localMinDate
        },
        localStartMaxDate() {

            return this.value.end ? this.value.end : this.localMaxDate
        },
        localEndMinDate() {

            return this.value.start ? this.value.start : this.localMinDate
        },
        localEndMaxDate() {

            if (this.value.start && !this.noDefaultRangeLimit) {

                const startPlusOneYear = moment(this.value.start).add(1, 'year').toDate()

                if (!this.localMaxDate || this.localMaxDate > startPlusOneYear) {

                    return startPlusOneYear
                }
            }

            return this.localMaxDate
        },
        localValidations() {

            const validations = {}

            if (this.localMinDate) {

                validations.min_date = this.localMinDate
            }

            if (this.localMaxDate) {

                validations.max_date = this.localMaxDate
            }

            return Object.assign({}, this.validations, validations)
        },
        localStartValidations() {

            const validations = {}

            if (this.localStartMinDate) {

                validations.min_date = this.localStartMinDate
            }

            if (this.value.end) {

                validations.max_date = this.value.end
            }
            else if (this.localStartMaxDate) {

                validations.max_date = this.localStartMaxDate
            }

            return Object.assign({}, this.startValidations, validations)
        },
        localEndValidations() {

            const validations = {}

            if (this.localEndMaxDate) {

                validations.max_date = this.localEndMaxDate
            }

            if (this.value.start) {

                validations.min_date = this.value.start
            }
            else if (this.localStartMinDate) {

                validations.min_date = this.localStartMinDate
            }

            return Object.assign({}, this.endValidations, validations)
        },
        formattedValue() {

            const date = moment(this.value)

            return date.isValid() ? date.format(this.dateFormat) : ''
        },
        formattedStartValue() {

            const date = moment(this.value.start)

            return date.isValid() ? date.format(this.dateFormat) : ''
        },
        formattedEndValue() {

            const date = moment(this.value.end)

            return date.isValid() ? date.format(this.dateFormat) : ''
        },
        localStartLabel() {
            return this.startLabel || 'Início'
        },
        localEndLabel() {
            return this.endLabel || 'Fim'
        },
        rangeOptionalProps() {

            const props = {}

            if (this.rangeInline) {

                props['label-cols'] = 'auto'
            }

            return props
        },
        tooltipPosition() {
            if(this.tooltipTop)
                return 'top'
            else if(this.tooltipRight)
                return 'right'
            else if(this.tooltipBottom)
                return 'bottom'
            else if(this.tooltipLeft)
                return 'left'

            return ''
        },
        tooltipText() {
            return this.tooltipTop
                || this.tooltipRight
                || this.tooltipBottom
                || this.tooltipLeft
        },
    },
    data: () => ({
        labelProperties: {
            'label-calendar': 'Calendário',
            'label-close-button': 'Fechar',
            'label-current-month': 'Mês atual',
            'label-help': 'Use as teclas de cursor para navegar pela data do calendário',
            'label-nav': 'Navegação no calendário',
            'label-next-decade': 'Próxima década',
            'label-next-month': 'Próximo mês',
            'label-next-year': 'Próximo ano',
            'label-no-date-selected': 'Nenhuma data selecionada',
            'label-prev-decade': 'Década anterior',
            'label-prev-month': 'Mês anterior',
            'label-prev-year': 'Ano anterior',
            'label-reset-button': 'Resetar',
            'label-today': 'Hoje',
            'label-today-button': 'Selecionar hoje',
        },
    }),
    methods: {
        handleBlur(event) {

            const date = moment(event.target.value, this.dateFormat)

            if (date.isValid()) {

                this.$emit('input', date.toDate())
            }
            else {

                event.target.value = null

                this.$emit('input', event.target.value)
            }

            this.$emit('blur', event)
        },
        handleStartBlur(event) {

            const date = moment(event.target.value, this.dateFormat)

            if (date.isValid()) {

                this.value.start = date.toDate()
            }
            else {

                this.value.start = event.target.value = null
            }

            this.$emit('blur', event)
        },
        handleEndBlur(event) {

            const date = moment(event.target.value, this.dateFormat)

            if (date.isValid()) {

                this.value.end = date.toDate()
            }
            else {

                this.value.end = event.target.value = null
            }

            this.$emit('blur', event)
        },
    },
    mounted() {

        const cleaveOptions = {
            date: true,
            datePattern: ['d', 'm', 'Y'],
            delimiter: '/'
        }

        if (this.range) {

            new Cleave(this.$refs.DateStartInput.$el, cleaveOptions)
            new Cleave(this.$refs.DateEndInput.$el, cleaveOptions)
        }
        else {

            new Cleave(this.$refs.DateInput.$el, cleaveOptions)
        }
    },
    props: {
        dateFormat: { type: String, default: 'DD/MM/YYYY' },
        disabled: { type: Boolean },
        endLabel: { type: String },
        endValidations: { type: Object },
        label: { type: String },
        maxDate: { type: String },
        minDate: { type: String },
        name: { type: String },
        noDefaultRangeLimit: { type: Boolean, default: false },
        range: { type: Boolean },
        rangeInline: { type: Boolean, default: true },
        readonly: { type: Boolean },
        right: { type: Boolean },
        startLabel: { type:String },
        startValidations: { type: Object },
        tooltipBottom: { type: String },
        tooltipLeft: { type: String },
        tooltipRight: { type: String },
        tooltipTop: { type: String },
        validationMode: { type: String, default: 'aggressive' },
        validations: { type: Object },
        value: { type: [Date, Object, String], default: null },
    },
}
</script>

<style>
.rkm-date-picker > .dropdown-menu.dropdown-menu-right {
    left: auto !important;
    right: auto !important;
}
</style>